// TODO: vitest component test (pages are covered by Playwright e2e)
import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { Resolver } from 'react-hook-form'
import { z } from 'zod'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import {
  Alert,
  Button,
  Card,
  DateInput,
  FormActions,
  Input,
  Select,
  Textarea,
  Skeleton,
  SearchableDropdown,
  Modal,
} from '@/components/ui'
import { FileUpload } from '@/components/ui/FileUpload'
import { useExpense, useCreateExpense, useUpdateExpense } from '@/hooks/useExpenses'
import { useProperties } from '@/hooks/useProperties'
import { useMaintenanceRequests } from '@/hooks/useMaintenanceRequests'
import { useLease } from '@/hooks/useLeases'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'
import { DEFAULT_EXPENSE_CATEGORIES } from '@/hooks/useExpenseCategorySettings'
import { api } from '@/lib/api'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning'
import { useFormTelemetry } from '@/hooks/useFormTelemetry'
import { useVendors, useCreateVendor, cmmsKeys } from '@/hooks/useCmms'
import { useQueryClient } from '@tanstack/react-query'
import type { Vendor } from '@/types'
import { formatBaht } from '@/lib/money'

interface ExpenseFormData {
  property_id: string
  category: string
  amount: number
  vat_amount: number
  transaction_fee: number
  expense_date: string
  vendor_name?: string
  vendor_id?: string
  wht_rate?: string
  paid_by?: string
  description?: string
  payment_method?: string
  notes?: string
  maintenance_request_id?: string
}

const expenseFormSchema = z.object({
  property_id: z.string().min(1),
  category: z.string().min(1),
  amount: z.coerce.number().positive(),
  vat_amount: z.coerce.number().min(0).default(0),
  transaction_fee: z.coerce.number().min(0).default(0),
  expense_date: z.string().min(1),
  vendor_name: z.string().optional(),
  vendor_id: z.string().optional(),
  wht_rate: z.string().optional(),
  paid_by: z.string().optional(),
  description: z.string().optional(),
  payment_method: z.string().optional(),
  notes: z.string().optional(),
  maintenance_request_id: z.string().optional(),
}) satisfies z.ZodType<ExpenseFormData>

async function uploadExpenseImage(file: File): Promise<string> {
  const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
    content_type: file.type,
  })
  await fetch(presigned.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  })
  return `/api/images/${presigned.key}`
}

// WHT preset rates for Thai 50 ทวิ certificate
const WHT_PRESETS = [
  { value: '', label: '' }, // populated as "None" from i18n
  { value: '0.01', label: '' },
  { value: '0.015', label: '' },
  { value: '0.02', label: '' },
  { value: '0.03', label: '' },
  { value: '0.05', label: '' },
  { value: '0.10', label: '' },
  { value: '__custom__', label: '' },
]

export function ExpenseFormPage() {
  const { t } = useTranslation('expenses')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id
  const expenseId = Number(id)
  const navigate = useNavigate()
  const translateError = useApiErrorTranslator('expenses')
  const addToast = useNotificationStore((s) => s.addToast)
  const queryClient = useQueryClient()

  // EP-130 (T130-014) — the lease deposit-settlement deep-link:
  // /expenses/new?lease_id={id}&category=deposit_return&amount={n}&property_id={id}
  const [searchParams] = useSearchParams()
  const leaseIdParam = searchParams.get('lease_id')
  const leaseId = leaseIdParam ? Number(leaseIdParam) : undefined
  const categoryParam = searchParams.get('category')
  const amountParam = searchParams.get('amount')
  const propertyIdParam = searchParams.get('property_id')
  // Bank reconciliation's "create expense" CTA carries the bank row's own date
  // across, so the new expense lines up with the withdrawal it will reconcile
  // against instead of defaulting to today.
  const expenseDateParam = searchParams.get('expense_date')

  const { data: existingExpense, isLoading: isLoadingExpense } = useExpense(
    isEditing ? expenseId : 0
  )
  const createExpense = useCreateExpense()
  const updateExpense = useUpdateExpense()

  // Create mode resolves the lease from the deep-link query param; edit mode
  // shows the lease already recorded on the expense (read-only either way).
  const { data: prefillLease } = useLease(leaseId ?? 0, !isEditing && !!leaseId)
  const linkedLease = isEditing
    ? (existingExpense?.lease ?? null)
    : prefillLease
      ? { id: prefillLease.id, lease_code: prefillLease.lease_code, unit_number: prefillLease.unit?.number ?? null }
      : null

  const { data: propertiesData } = useProperties()
  const { data: accountSettings } = useAccountSettings()
  const { format, timezone, calendar } = useLocalizationConfig()

  // Maintenance requests for linking - load all open/in_progress ones
  const { data: maintenanceData } = useMaintenanceRequests(1)

  // Vendor search state
  const [vendorSearch, setVendorSearch] = useState('')
  const [vendorId, setVendorId] = useState('')
  const [showManualVendorName, setShowManualVendorName] = useState(false)
  const [showCreateVendorModal, setShowCreateVendorModal] = useState(false)
  const [newVendorName, setNewVendorName] = useState('')
  const [newVendorTaxId, setNewVendorTaxId] = useState('')
  const [accumulatedVendors, setAccumulatedVendors] = useState<Vendor[]>([])
  const [vendorPage, setVendorPage] = useState(1)

  const { data: vendorsData, isFetching: isLoadingVendors } = useVendors(vendorPage, {
    q: vendorSearch || undefined,
  })
  const createVendor = useCreateVendor()

  // WHT state
  const [whtRateValue, setWhtRateValue] = useState('')
  const [customWhtRate, setCustomWhtRate] = useState('')

  // null means "not yet edited by user" — derive from server data
  const [localImageUrls, setImageUrls] = useState<string[] | null>(null)
  const [localReceiptImageUrls, setReceiptImageUrls] = useState<string[] | null>(null)

  const imageUrls = localImageUrls ?? (isEditing && existingExpense ? (existingExpense.image_urls ?? []) : [])
  const receiptImageUrls = localReceiptImageUrls ?? (isEditing && existingExpense ? (existingExpense.receipt_image_urls ?? []) : [])

  const properties = propertiesData?.data || []
  const maintenanceRequests = maintenanceData?.data || []

  // Resolve category options from account settings or defaults
  const configuredCategories = accountSettings?.settings?.expense_categories
  const categoryList =
    configuredCategories && configuredCategories.length > 0
      ? configuredCategories
      : DEFAULT_EXPENSE_CATEGORIES

  const today = localToday(timezone)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ExpenseFormData>({
    resolver: zodResolver(expenseFormSchema) as unknown as Resolver<ExpenseFormData>,
    defaultValues: {
      property_id: propertyIdParam ?? '',
      category: categoryParam ?? '',
      amount: amountParam ? Number(amountParam) : 0,
      vat_amount: 0,
      transaction_fee: 0,
      expense_date: expenseDateParam ?? today,
      vendor_name: '',
      vendor_id: '',
      wht_rate: '',
      paid_by: '',
      description: '',
      payment_method: '',
      notes: '',
      maintenance_request_id: '',
    },
  })

  const amountValue = watch('amount')
  const categoryValue = watch('category')
  // BA-4: a deposit-return expense never carries a vendor or a withholding
  // tax deduction — the tenant is being paid back, not a vendor.
  const isDepositReturn = categoryValue === 'deposit_return'

  // WEB-M3: warn before tab close/refresh with unsaved edits (beforeunload).
  useUnsavedChangesWarning(isDirty)

  // EP-73 Tier-1: form_started:expense / form_submitted:expense lifecycle.
  const { markStarted, markSubmitted } = useFormTelemetry('expense')

  // Vendor search: reset page and accumulated list when query changes
  useEffect(() => {
    setVendorPage(1)
    setAccumulatedVendors([])
  }, [vendorSearch])

  // Append new vendor page results
  useEffect(() => {
    if (!vendorsData?.data) return
    setAccumulatedVendors((prev) => {
      if (vendorPage === 1) return vendorsData.data
      const existingIds = new Set(prev.map((v) => v.id))
      const newItems = vendorsData.data.filter((v) => !existingIds.has(v.id))
      return [...prev, ...newItems]
    })
  }, [vendorsData, vendorPage])

  // Populate form when editing
  useEffect(() => {
    if (isEditing && existingExpense) {
      reset({
        property_id: String(existingExpense.property_id),
        category: existingExpense.category,
        amount: existingExpense.amount,
        vat_amount: existingExpense.vat_amount,
        transaction_fee: existingExpense.transaction_fee ?? 0,
        expense_date: existingExpense.expense_date,
        vendor_name: existingExpense.vendor_name ?? '',
        vendor_id: existingExpense.vendor_id ? String(existingExpense.vendor_id) : '',
        wht_rate: existingExpense.wht_rate ? String(existingExpense.wht_rate) : '',
        paid_by: existingExpense.paid_by ?? '',
        description: existingExpense.description ?? '',
        payment_method: existingExpense.payment_method ?? '',
        notes: existingExpense.notes ?? '',
        maintenance_request_id: existingExpense.maintenance_request_id
          ? String(existingExpense.maintenance_request_id)
          : '',
      })
      // Restore vendor selection
      if (existingExpense.vendor_id) {
        setVendorId(String(existingExpense.vendor_id))
        // Ensure the saved vendor is in the accumulated list
        if (existingExpense.vendor_name_resolved) {
          setAccumulatedVendors([{
            id: existingExpense.vendor_id,
            name: existingExpense.vendor_name_resolved,
          } as Vendor])
        }
      } else if (existingExpense.vendor_name) {
        setShowManualVendorName(true)
      }
      // Restore WHT rate
      if (existingExpense.wht_rate) {
        const rate = String(existingExpense.wht_rate)
        const preset = WHT_PRESETS.find((p) => p.value === rate && p.value !== '__custom__')
        if (preset) {
          setWhtRateValue(rate)
        } else {
          setWhtRateValue('__custom__')
          setCustomWhtRate(rate)
        }
      }
    }
  }, [isEditing, existingExpense, reset])

  // BA-4: clear vendor + WHT selections when switching to deposit_return so
  // the hidden sections never submit stale values.
  useEffect(() => {
    if (!isDepositReturn) return
    setVendorId('')
    setShowManualVendorName(false)
    setValue('vendor_name', '')
    setWhtRateValue('')
    setCustomWhtRate('')
  }, [isDepositReturn, setValue])

  // Computed WHT display values (display-only — server recomputes authoritatively)
  const effectiveWhtRate = whtRateValue === '__custom__'
    ? parseFloat(customWhtRate) || 0
    : parseFloat(whtRateValue) || 0
  const displayWhtAmount = Math.round(Number(amountValue || 0) * effectiveWhtRate * 100) / 100
  const displayNetCashAmount = Math.round((Number(amountValue || 0) - displayWhtAmount) * 100) / 100

  const handleCreateVendor = async () => {
    if (!newVendorName.trim()) return
    try {
      const vendor = await createVendor.mutateAsync({
        name: newVendorName.trim(),
        // tax_id is not in the CreateVendorInput interface yet; send via the API directly
      })
      // If tax_id was entered, update via PATCH
      if (newVendorTaxId.trim()) {
        await api.patch(`/api/vendors/${vendor.id}`, { vendor: { tax_id: newVendorTaxId.trim() } })
      }
      // Auto-select the new vendor
      setVendorId(String(vendor.id))
      setAccumulatedVendors((prev) => {
        const exists = prev.find((v) => v.id === vendor.id)
        if (exists) return prev
        return [vendor, ...prev]
      })
      queryClient.invalidateQueries({ queryKey: cmmsKeys.vendors.lists() })
      setShowCreateVendorModal(false)
      setNewVendorName('')
      setNewVendorTaxId('')
      addToast({ type: 'success', message: tc('messages.created') })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const handleVendorSearch = useCallback((q: string) => {
    setVendorSearch(q)
  }, [])

  const onSubmit = async (data: ExpenseFormData) => {
    // Resolve WHT rate: custom input vs preset
    let resolvedWhtRate: number | null | undefined
    if (whtRateValue === '__custom__') {
      resolvedWhtRate = customWhtRate ? parseFloat(customWhtRate) : null
    } else if (whtRateValue) {
      resolvedWhtRate = parseFloat(whtRateValue)
    } else {
      resolvedWhtRate = null
    }

    const payload = {
      property_id: Number(data.property_id),
      category: data.category,
      amount: data.amount,
      vat_amount: data.vat_amount,
      transaction_fee: data.transaction_fee ?? 0,
      expense_date: data.expense_date,
      vendor_name: (!vendorId && data.vendor_name) ? data.vendor_name || undefined : undefined,
      vendor_id: vendorId ? Number(vendorId) : null,
      wht_rate: resolvedWhtRate,
      paid_by: data.paid_by || undefined,
      description: data.description || undefined,
      payment_method: data.payment_method || undefined,
      notes: data.notes || undefined,
      image_urls: imageUrls,
      receipt_image_urls: receiptImageUrls,
      maintenance_request_id:
        data.maintenance_request_id ? Number(data.maintenance_request_id) : undefined,
    }

    try {
      if (isEditing) {
        await updateExpense.mutateAsync({ id: expenseId, ...payload })
        markSubmitted()
        navigate(`/expenses/${expenseId}`)
      } else {
        const result = await createExpense.mutateAsync({ ...payload, lease_id: leaseId ?? undefined })
        markSubmitted()
        navigate(`/expenses/${result.id}`)
      }
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const propertyOptions = [
    { value: '', label: tc('actions.select') + ' ' + t('labels.property') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  const categoryOptions = [
    { value: '', label: tc('actions.select') + ' ' + t('labels.category') },
    ...categoryList.map((cat) => ({
      value: cat.key,
      label: i18n.language === 'th' ? cat.th : cat.en,
    })),
  ]

  const paymentMethodOptions = [
    { value: '', label: '-' },
    { value: 'cash', label: t('payment_methods.cash') },
    { value: 'bank_transfer', label: t('payment_methods.bank_transfer') },
    { value: 'cheque', label: t('payment_methods.cheque') },
    { value: 'credit_card', label: t('payment_methods.credit_card') },
    { value: 'direct_debit', label: t('payment_methods.direct_debit') },
    { value: 'other', label: t('payment_methods.other') },
  ]

  const maintenanceOptions = [
    { value: '', label: '-' },
    ...maintenanceRequests.map((mr) => ({
      value: String(mr.id),
      label: `#${mr.id} ${mr.title}`,
    })),
  ]

  const whtPresetOptions = [
    { value: '', label: t('wht_presets.none') },
    { value: '0.01', label: t('wht_presets.rate_1') },
    { value: '0.015', label: t('wht_presets.rate_1_5') },
    { value: '0.02', label: t('wht_presets.rate_2') },
    { value: '0.03', label: t('wht_presets.rate_3') },
    { value: '0.05', label: t('wht_presets.rate_5') },
    { value: '0.10', label: t('wht_presets.rate_10') },
    { value: '__custom__', label: t('wht_presets.custom') },
  ]

  // Vendor dropdown options — include saved selection when editing (preserve on edit)
  const vendorOptions = (() => {
    const opts = accumulatedVendors.map((v) => ({
      value: String(v.id),
      label: v.name + (v.contact_name ? ` (${v.contact_name})` : ''),
    }))
    // Preserve saved vendor on edit if not in loaded page
    if (
      isEditing &&
      existingExpense?.vendor_id &&
      existingExpense.vendor_name_resolved &&
      !opts.find((o) => o.value === String(existingExpense.vendor_id))
    ) {
      opts.unshift({
        value: String(existingExpense.vendor_id),
        label: existingExpense.vendor_name_resolved,
      })
    }
    return opts
  })()

  if (isEditing && isLoadingExpense) {
    return (
        <div className="space-y-6">
          <Card>
            <Skeleton className="h-64" />
          </Card>
        </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: t('title'), href: '/expenses' },
            {
              label: isEditing ? t('actions.edit_expense') : t('actions.new_expense'),
            },
          ]}
        />

        <PageHeader
          title={isEditing ? t('actions.edit_expense') : t('actions.new_expense')}
        />

        {linkedLease && (
          <Alert variant="info">
            <span>
              {t('lease_chip.linked_to', {
                leaseCode: linkedLease.lease_code,
                unitNumber: linkedLease.unit_number ?? '-',
              })}
            </span>
            <Link to={`/leases/${linkedLease.id}`} className="link link-primary font-medium">
              {t('lease_chip.view_lease')}
            </Link>
          </Alert>
        )}

        <Card>
          <form onSubmit={handleSubmit(onSubmit)} onChangeCapture={markStarted} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Property */}
              <Select
                label={t('labels.property')}
                options={propertyOptions}
                error={errors.property_id?.message ? tc('errors.required') : undefined}
                disabled={isEditing}
                {...register('property_id')}
              />

              {/* Category */}
              <Select
                label={t('labels.category')}
                options={categoryOptions}
                error={errors.category?.message ? tc('errors.required') : undefined}
                {...register('category')}
              />

              {/* Expense Date */}
              <DateInput
                label={t('labels.date')}
                error={errors.expense_date?.message ? tc('errors.required') : undefined}
                defaultValue={isEditing && existingExpense ? existingExpense.expense_date : (expenseDateParam ?? today)}
                dateFormat={format}
                timezone={timezone}
                calendar={calendar}
                locale={i18n.language}
                {...register('expense_date')}
              />

              {/* Payment Method */}
              <Select
                label={t('labels.payment_method')}
                options={paymentMethodOptions}
                {...register('payment_method')}
              />

              {/* Amount */}
              <Input
                type="number"
                step="0.01"
                min="0.01"
                label={t('labels.amount')}
                error={errors.amount?.message ? tc('errors.required') : undefined}
                {...register('amount')}
              />

              {/* VAT Amount */}
              <Input
                type="number"
                step="0.01"
                min="0"
                label={t('labels.vat_amount')}
                {...register('vat_amount')}
              />

              {/* Transaction Fee */}
              <Input
                type="number"
                step="0.01"
                min="0"
                label={t('labels.transaction_fee')}
                placeholder={t('placeholders.transaction_fee')}
                {...register('transaction_fee')}
              />

              {/* Paid By */}
              <Input
                label={t('labels.paid_by')}
                placeholder={t('placeholders.paid_by')}
                {...register('paid_by')}
              />

              {/* Linked Maintenance */}
              <Select
                label={t('labels.maintenance_request')}
                options={maintenanceOptions}
                {...register('maintenance_request_id')}
              />
            </div>

            {/* Vendor Section — hidden for deposit_return (BA-4): a deposit
                is returned to the tenant, not paid to a vendor. */}
            {!isDepositReturn && (
              <div className="space-y-3">
                <div className="min-w-0">
                  <SearchableDropdown
                    label={t('labels.vendor')}
                    placeholder={t('placeholders.search_vendor')}
                    options={vendorOptions}
                    value={vendorId}
                    onChange={setVendorId}
                    onSearch={handleVendorSearch}
                    isLoading={isLoadingVendors}
                    onLoadMore={() => setVendorPage((p) => p + 1)}
                    hasMore={!!vendorsData?.meta && vendorPage < (vendorsData.meta.total_pages ?? 1)}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowManualVendorName((v) => !v)}
                  >
                    {t('actions.enter_manually')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCreateVendorModal(true)}
                  >
                    + {t('actions.create_vendor')}
                  </Button>
                </div>
                {showManualVendorName && (
                  <div className="min-w-0">
                    <Input
                      label={t('labels.vendor_name')}
                      placeholder={t('placeholders.vendor_name')}
                      {...register('vendor_name')}
                    />
                  </div>
                )}
              </div>
            )}

            {/* WHT Section — hidden for deposit_return (BA-4): the return is
                not vendor income, so no withholding tax applies. */}
            {!isDepositReturn && (
              <div className="space-y-3">
                <div className="min-w-0">
                  <Select
                    id="wht_rate"
                    label={t('labels.wht_rate')}
                    options={whtPresetOptions}
                    value={whtRateValue}
                    onChange={(e) => setWhtRateValue(e.target.value)}
                  />
                </div>
                {whtRateValue === '__custom__' && (
                  <div className="min-w-0">
                    <Input
                      id="wht_rate_custom"
                      type="number"
                      step="0.001"
                      min="0"
                      max="1"
                      label={t('labels.wht_rate')}
                      placeholder={t('placeholders.wht_rate_custom')}
                      value={customWhtRate}
                      onChange={(e) => setCustomWhtRate(e.target.value)}
                    />
                  </div>
                )}
                {effectiveWhtRate > 0 && (
                  <div className="grid gap-3 sm:grid-cols-2 bg-base-200/50 rounded-lg p-3">
                    <div className="min-w-0">
                      <div className="text-sm text-base-content/60">{t('labels.wht_amount')}</div>
                      <div className="font-medium text-base-content/80">
                        {formatBaht(displayWhtAmount, { locale: 'en', decimals: 2 })}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm text-base-content/60">{t('labels.net_cash_amount')}</div>
                      <div className="font-medium text-base-content/80">
                        {formatBaht(displayNetCashAmount, { locale: 'en', decimals: 2 })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            <Textarea
              label={t('labels.description')}
              placeholder={t('placeholders.description')}
              rows={3}
              {...register('description')}
            />

            {/* Notes */}
            <Textarea
              label={t('labels.notes')}
              placeholder={t('placeholders.notes')}
              rows={3}
              {...register('notes')}
            />

            {/* Receipt / Tax Invoice Images */}
            <FileUpload
              label={t('labels.receipt_images')}
              value={receiptImageUrls}
              onChange={setReceiptImageUrls}
              maxFiles={5}
              accept="image/*"
              onUpload={uploadExpenseImage}
            />

            {/* Supporting Photos (evidence) */}
            <FileUpload
              label={t('labels.evidence_images')}
              value={imageUrls}
              onChange={setImageUrls}
              maxFiles={5}
              accept="image/*"
              onUpload={uploadExpenseImage}
            />

            <FormActions>
              <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
                {tc('actions.cancel')}
              </Button>
              <Button
                type="submit"
                loading={isEditing ? updateExpense.isPending : createExpense.isPending}
              >
                {isEditing ? tc('actions.save') : tc('actions.create')}
              </Button>
            </FormActions>
          </form>
        </Card>
      </div>

      {/* Inline Create Vendor Modal */}
      <Modal
        isOpen={showCreateVendorModal}
        onClose={() => {
          setShowCreateVendorModal(false)
          setNewVendorName('')
          setNewVendorTaxId('')
        }}
        title={t('actions.create_vendor')}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setShowCreateVendorModal(false)
                setNewVendorName('')
                setNewVendorTaxId('')
              }}
            >
              {tc('actions.cancel')}
            </Button>
            <Button
              onClick={handleCreateVendor}
              loading={createVendor.isPending}
              disabled={!newVendorName.trim()}
            >
              {tc('actions.create')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label={t('labels.vendor_name')}
            value={newVendorName}
            onChange={(e) => setNewVendorName(e.target.value)}
            autoFocus
          />
          <Input
            label="Tax ID"
            value={newVendorTaxId}
            onChange={(e) => setNewVendorTaxId(e.target.value)}
            placeholder="13-digit Thai TIN (optional)"
          />
        </div>
      </Modal>
    </>
  )
}
